import { isBrowserEnvironment } from './environment';

export type ScreenshotResult = {
  base64: string | null;
  failureReason?: string;
};

const TIMEOUT_MS = 1000;
const MAX_BYTES = 1_400_000;

export async function captureScreenshot(): Promise<ScreenshotResult> {
  if (!isBrowserEnvironment()) {
    return { base64: null, failureReason: 'unsupported environment' };
  }
  try {
    const html2canvas = (await import('html2canvas')).default;
    const target = document.body;
    if (!target) return { base64: null, failureReason: 'no document.body' };

    const canvasPromise = html2canvas(target, {
      logging: false,
      useCORS: true,
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      backgroundColor: '#ffffff',
    });

    const canvas = await withTimeout(canvasPromise, TIMEOUT_MS);
    if (!canvas) return { base64: null, failureReason: 'timeout' };

    for (const quality of [0.6, 0.45, 0.3]) {
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_BYTES) {
        return { base64: dataUrl };
      }
    }
    return { base64: null, failureReason: 'exceeded size budget' };
  } catch (err) {
    return {
      base64: null,
      failureReason: err instanceof Error ? err.message : String(err),
    };
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, ms);

    p.then(
      (v) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(null);
      }
    );
  });
}
